// Copyright (c) 2026, IPivot. All rights reserved.
'use strict';

import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Badge, ButtonGroup, Spinner } from 'react-bootstrap';
import { diffLines } from 'diff';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { PendingChange, getCategory, getFingerprint, getFingerprintsThin, getMatchesForFinger } from '../data';

// ── Line-level inline diff ─────────────────────────────────────────────────

type DiffLineKind = 'added' | 'removed' | 'same';

const LINE_DIFF_STYLES: Record<DiffLineKind, { marker: string; background: string; color: string }> = {
    added: { marker: '+', background: '#0f2e1a', color: '#6ee7b7' },
    removed: { marker: '−', background: '#2e0f0f', color: '#fca5a5' },
    same: { marker: ' ', background: 'transparent', color: '#9ca3af' },
};

function LineDiff({ from, to }: { from: string; to: string }) {
    // diffLines hands back multi-line chunks; split them so each rendered row is one line.
    const lines: { kind: DiffLineKind; text: string }[] = [];
    for (const part of diffLines(from ?? '', to ?? '')) {
        const kind: DiffLineKind = part.added ? 'added' : part.removed ? 'removed' : 'same';
        for (const text of part.value.replace(/\n$/, '').split('\n')) {
            lines.push({ kind, text });
        }
    }

    return (
        <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: '1.5' }}>
            {lines.map((line, i) => {
                const style = LINE_DIFF_STYLES[line.kind];
                return (
                    <div key={i} style={{ display: 'flex', background: style.background, color: style.color, borderRadius: 2 }}>
                        <span style={{ flex: '0 0 auto', padding: '0 4px', opacity: 0.7, userSelect: 'none' }}>{style.marker}</span>
                        <span style={{ flex: '1 1 auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingRight: 4 }}>
                            {line.text || ' '}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// ── JSON tree diff viewer ──────────────────────────────────────────────────

type TreeNode =
    | { kind: 'unchanged'; key: string; value: any; depth: number }
    | { kind: 'changed'; key: string; from: any; to: any; depth: number }
    | { kind: 'added'; key: string; value: any; depth: number }
    | { kind: 'removed'; key: string; value: any; depth: number };

function buildJsonDiffNodes(before: any, after: any, depth = 0, prefix = ''): TreeNode[] {
    const nodes: TreeNode[] = [];
    const bObj = before && typeof before === 'object' && !Array.isArray(before) ? before : null;
    const aObj = after && typeof after === 'object' && !Array.isArray(after) ? after : null;
    if (!bObj && !aObj) {
        const bs = JSON.stringify(before);
        const as_ = JSON.stringify(after);
        if (bs !== as_) nodes.push({ kind: 'changed', key: prefix || '(value)', from: before, to: after, depth });
        else nodes.push({ kind: 'unchanged', key: prefix || '(value)', value: before, depth });
        return nodes;
    }
    const allKeys = new Set([...Object.keys(bObj || {}), ...Object.keys(aObj || {})]);
    for (const key of allKeys) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const inBefore = bObj && Object.prototype.hasOwnProperty.call(bObj, key);
        const inAfter = aObj && Object.prototype.hasOwnProperty.call(aObj, key);
        const bv = bObj?.[key];
        const av = aObj?.[key];
        if (!inBefore) {
            nodes.push({ kind: 'added', key: fullKey, value: av, depth });
        } else if (!inAfter) {
            nodes.push({ kind: 'removed', key: fullKey, value: bv, depth });
        } else if (bv !== null && av !== null && typeof bv === 'object' && typeof av === 'object' && !Array.isArray(bv) && !Array.isArray(av)) {
            nodes.push(...buildJsonDiffNodes(bv, av, depth + 1, fullKey));
        } else {
            const bs = JSON.stringify(bv);
            const as_ = JSON.stringify(av);
            if (bs === as_) nodes.push({ kind: 'unchanged', key: fullKey, value: bv, depth });
            else nodes.push({ kind: 'changed', key: fullKey, from: bv, to: av, depth });
        }
    }
    return nodes;
}

function JsonCellValue({ value, color }: { value: any; color: string }) {
    if (value !== null && typeof value === 'object') {
        return (
            <div style={{ border: '1px solid #30363d', borderRadius: 4, padding: '4px 6px', background: '#0d1117' }}>
                <JsonEditor
                    data={value}
                    viewOnly
                    collapse={1}
                    theme={githubDarkTheme}
                />
            </div>
        );
    }
    return <span style={{ color }}>{JSON.stringify(value)}</span>;
}

function JsonDiffTree({ before, after, emptyLabel = '—' }: { before: any; after: any; emptyLabel?: string }) {
    const [showUnchanged, setShowUnchanged] = useState(false);
    const nodes = buildJsonDiffNodes(before, after);
    const changedNodes = nodes.filter(n => n.kind !== 'unchanged');
    const unchangedCount = nodes.length - changedNodes.length;
    const displayed = showUnchanged ? nodes : changedNodes;

    if (nodes.length === 0) return <span className="text-muted small">{emptyLabel}</span>;
    const allEmpty = nodes.length === 1 && nodes[0].kind === 'unchanged' && (nodes[0] as any).value == null;
    if (allEmpty) return <span className="text-muted small">{emptyLabel}</span>;

    return (
        <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: '1.6' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '32.5%' }} />
                    <col style={{ width: '32.5%' }} />
                </colgroup>
                <thead>
                    <tr style={{ borderBottom: '1px solid #333', color: '#aaa', fontSize: '0.72rem' }}>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>key</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400, color: '#fca5a5' }}>before</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400, color: '#6ee7b7' }}>after</th>
                    </tr>
                </thead>
                <tbody>
                    {displayed.map((node, i) => {
                        const indent = '\u00a0'.repeat(node.depth * 2);
                        const bg = node.kind === 'added' ? '#0f2e1a'
                            : node.kind === 'removed' ? '#2e0f0f'
                            : node.kind === 'changed' ? '#1a1a00'
                            : 'transparent';
                        const fromVal = (node.kind === 'changed') ? (node as any).from
                            : (node.kind === 'removed') ? (node as any).value : undefined;
                        const toVal = (node.kind === 'changed') ? (node as any).to
                            : (node.kind === 'added') ? (node as any).value : undefined;
                        const unchangedVal = (node.kind === 'unchanged') ? (node as any).value : undefined;
                        const fromStr = JSON.stringify(fromVal);
                        const toStr = JSON.stringify(toVal);
                        const shortKey = node.key.split('.').pop() ?? node.key;
                        const showObjectDiff = node.kind === 'changed' && fromVal !== null && toVal !== null
                            && typeof fromVal === 'object' && typeof toVal === 'object';

                        return (
                            <tr key={i} style={{ background: bg }}>
                                <td style={{ padding: '1px 4px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={node.key}>
                                    {indent}{shortKey}
                                </td>
                                <td style={{ padding: '1px 4px', color: node.kind === 'unchanged' ? '#6b7280' : '#fca5a5', wordBreak: 'break-all' }}>
                                    {node.kind === 'unchanged'
                                        ? <JsonCellValue value={unchangedVal} color="#6b7280" />
                                        : node.kind === 'added'
                                            ? ''
                                            : <JsonCellValue value={fromVal} color="#fca5a5" />}
                                </td>
                                <td style={{ padding: '1px 4px', color: node.kind === 'unchanged' ? '#6b7280' : '#6ee7b7', wordBreak: 'break-all' }}>
                                    {node.kind === 'changed'
                                        ? showObjectDiff
                                            ? <JsonCellValue value={toVal} color="#6ee7b7" />
                                            : <LineDiff from={fromStr} to={toStr} />
                                        : node.kind === 'unchanged'
                                            ? <JsonCellValue value={unchangedVal} color="#6b7280" />
                                            : <JsonCellValue value={toVal} color="#6ee7b7" />}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {unchangedCount > 0 && (
                <button className="btn btn-link btn-sm p-0 mt-1" style={{ fontSize: '0.72rem', color: '#6b7280' }}
                    onClick={() => setShowUnchanged(v => !v)}>
                    {showUnchanged ? `▲ hide ${unchangedCount} unchanged` : `▼ show ${unchangedCount} unchanged`}
                </button>
            )}
        </div>
    );
}

// ── Op banner ─────────────────────────────────────────────────────────────

function OpBanner({ op }: { op: string }) {
    if (op === 'add') {
        return (
            <div style={{ background: '#052e16', border: '1px solid #166534', borderRadius: 6, padding: '8px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem', color: '#86efac' }}>+</span>
                <span style={{ color: '#86efac', fontWeight: 600 }}>Added</span>
                <span className="text-muted small">New item being introduced</span>
            </div>
        );
    }
    if (op === 'delete') {
        return (
            <div style={{ background: '#2d0a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '8px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem', color: '#f87171' }}>−</span>
                <span style={{ color: '#f87171', fontWeight: 600 }}>Deleted</span>
                <span className="text-muted small">This item will be removed</span>
            </div>
        );
    }
    return null;
}

// ── Field row helpers ──────────────────────────────────────────────────────

function FieldRow({ label, from, to, op }: { label: string; from: any; to: any; op: string }) {
    const fromStr = typeof from === 'object' ? JSON.stringify(from) : String(from ?? '');
    const toStr = typeof to === 'object' ? JSON.stringify(to) : String(to ?? '');
    const changed = fromStr !== toStr;
    const displayStr = op === 'delete' ? fromStr : toStr;

    if (op === 'add') {
        return (
            <tr>
                <td className="text-muted small" style={{ width: '7rem', whiteSpace: 'nowrap', paddingRight: 8, verticalAlign: 'top' }}>{label}</td>
                <td style={{ color: '#86efac', wordBreak: 'break-word' }}>{toStr || <span className="text-muted">—</span>}</td>
            </tr>
        );
    }
    if (op === 'delete') {
        return (
            <tr>
                <td className="text-muted small" style={{ width: '7rem', whiteSpace: 'nowrap', paddingRight: 8, verticalAlign: 'top' }}>{label}</td>
                <td style={{ color: '#f87171', textDecoration: 'line-through', wordBreak: 'break-word' }}>{fromStr || <span className="text-muted">—</span>}</td>
            </tr>
        );
    }
    if (!changed) {
        return (
            <tr>
                <td className="text-muted small" style={{ width: '7rem', whiteSpace: 'nowrap', paddingRight: 8, verticalAlign: 'top' }}>{label}</td>
                <td className="text-muted" style={{ wordBreak: 'break-word' }}>{displayStr || <span style={{ opacity: 0.4 }}>—</span>}</td>
            </tr>
        );
    }
    return (
        <tr style={{ background: '#1c1c00' }}>
            <td className="small" style={{ width: '7rem', whiteSpace: 'nowrap', paddingRight: 8, verticalAlign: 'top', color: '#facc15' }}>{label}</td>
            <td style={{ wordBreak: 'break-word' }}>
                <LineDiff from={fromStr} to={toStr} />
            </td>
        </tr>
    );
}

// ── Matches section ────────────────────────────────────────────────────────

function matchKey(m: any): string {
    return [m.ip, m.reverse_dns, m.forward_dns, m.recent_dns, m.tls_cert_subj, m.tls_sni, m.tls_alpn]
        .filter(Boolean).join('|') || '(empty)';
}

function MatchesSection({ before, after, op }: { before: any[]; after: any[]; op: string }) {
    const [showSame, setShowSame] = useState(true);

    if (op === 'add') {
        if (after.length === 0) return null;
        return (
            <div>
                <div className="text-muted small mb-1">Matches <span style={{ color: '#6ee7b7' }}>+{after.length}</span></div>
                <MatchTable matches={after} rowColor="#0f2e1a" marker="+" markerColor="#6ee7b7" />
            </div>
        );
    }
    if (op === 'delete') {
        if (before.length === 0) return null;
        return (
            <div>
                <div className="text-muted small mb-1">Matches <span style={{ color: '#f87171' }}>−{before.length}</span></div>
                <MatchTable matches={before} rowColor="#2e0f0f" marker="−" markerColor="#f87171" />
            </div>
        );
    }

    // change
    const beforeMatchKeySet = new Set(before.map(matchKey));
    const afterMatchKeySet = new Set(after.map(matchKey));
    const added = after.filter(m => !beforeMatchKeySet.has(matchKey(m)));
    const removed = before.filter(m => !afterMatchKeySet.has(matchKey(m)));
    const same = after.filter(m => beforeMatchKeySet.has(matchKey(m)));
    const total = added.length + removed.length + same.length;
    if (total === 0) return null;

    return (
        <div>
            <div className="text-muted small mb-1">
                Matches
                {added.length > 0 && <span className="ms-1" style={{ color: '#6ee7b7' }}>+{added.length}</span>}
                {removed.length > 0 && <span className="ms-1" style={{ color: '#f87171' }}>−{removed.length}</span>}
                {same.length > 0 && <span className="ms-1" style={{ color: '#6b7280' }}>={same.length}</span>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: 'monospace' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #333', color: '#aaa' }}>
                        <th style={{ padding: '2px 4px', fontWeight: 400, width: 18 }}></th>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>IP</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>Rev DNS</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>Fwd DNS</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>TLS SNI</th>
                        <th style={{ padding: '2px 4px', fontWeight: 400 }}>ALPN</th>
                    </tr>
                </thead>
                <tbody>
                    {added.map((m, i) => <MatchRow key={`a-${i}`} m={m} bg="#0f2e1a" marker="+" mc="#6ee7b7" />)}
                    {removed.map((m, i) => <MatchRow key={`r-${i}`} m={m} bg="#2e0f0f" marker="−" mc="#f87171" />)}
                    {showSame && same.map((m, i) => <MatchRow key={`s-${i}`} m={m} bg="transparent" marker=" " mc="#6b7280" />)}
                </tbody>
            </table>
            {same.length > 0 && (
                <button className="btn btn-link btn-sm p-0 mt-1" style={{ fontSize: '0.72rem', color: '#6b7280' }}
                    onClick={() => setShowSame(v => !v)}>
                    {showSame ? `▲ hide ${same.length} unchanged` : `▼ show ${same.length} unchanged`}
                </button>
            )}
        </div>
    );
}

function MatchRow({ m, bg, marker, mc }: { m: any; bg: string; marker: string; mc: string }) {
    return (
        <tr style={{ background: bg }}>
            <td style={{ padding: '1px 4px', color: mc }}>{marker}</td>
            <td style={{ padding: '1px 4px' }}>{m.ip}</td>
            <td style={{ padding: '1px 4px' }}>{m.reverse_dns}</td>
            <td style={{ padding: '1px 4px' }}>{m.forward_dns}</td>
            <td style={{ padding: '1px 4px' }}>{m.tls_sni}</td>
            <td style={{ padding: '1px 4px' }}>{m.tls_alpn}</td>
        </tr>
    );
}

function MatchTable({ matches, rowColor, marker, markerColor }: { matches: any[]; rowColor: string; marker: string; markerColor: string }) {
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: 'monospace' }}>
            <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#aaa' }}>
                    <th style={{ padding: '2px 4px', fontWeight: 400, width: 18 }}></th>
                    <th style={{ padding: '2px 4px', fontWeight: 400 }}>IP</th>
                    <th style={{ padding: '2px 4px', fontWeight: 400 }}>Rev DNS</th>
                    <th style={{ padding: '2px 4px', fontWeight: 400 }}>Fwd DNS</th>
                    <th style={{ padding: '2px 4px', fontWeight: 400 }}>TLS SNI</th>
                    <th style={{ padding: '2px 4px', fontWeight: 400 }}>ALPN</th>
                </tr>
            </thead>
            <tbody>
                {matches.map((m, i) => <MatchRow key={i} m={m} bg={rowColor} marker={marker} mc={markerColor} />)}
            </tbody>
        </table>
    );
}

// ── Fingerprint diff ───────────────────────────────────────────────────────

function FingerprintDiff({ before, after, op }: { before: any; after: any; op: string }) {
    const src = op === 'delete' ? before : after;
    const scalarFields: [string, string][] = [
        ['PVKey', 'pvkey'], ['Vendor', 'vendor'], ['Product', 'product'],
        ['Title', 'title'], ['Description', 'description'],
        ['Layer', 'layer'], ['Res Type', 'res_type'],
    ];

    const beforeCats: string[] = before?.categories || [];
    const afterCats: string[] = after?.categories || [];
    const catsBefore = new Set(beforeCats);
    const catsAfter = new Set(afterCats);
    const addedCats = afterCats.filter(c => !catsBefore.has(c));
    const removedCats = beforeCats.filter(c => !catsAfter.has(c));
    const sameCats = beforeCats.filter(c => catsAfter.has(c));
    const hasCatChange = addedCats.length > 0 || removedCats.length > 0;
    const displayCats = op === 'delete' ? beforeCats : op === 'add' ? afterCats : (hasCatChange ? null : sameCats);

    const beforePresentation = before?.presentation ?? null;
    const afterPresentation = after?.presentation ?? null;
    const hasPresentation = (beforePresentation && Object.keys(beforePresentation).length > 0)
        || (afterPresentation && Object.keys(afterPresentation).length > 0);

    const beforeResTags = before?.res_tags ?? null;
    const afterResTags = after?.res_tags ?? null;
    const hasResTags = (beforeResTags && Object.keys(beforeResTags).length > 0)
        || (afterResTags && Object.keys(afterResTags).length > 0);

    const beforeMatches: any[] = before?.matches || [];
    const afterMatches: any[] = after?.matches || [];

    return (
        <div style={{ fontSize: '0.85rem' }}>
            <OpBanner op={op} />

            {/* Scalar fields */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
                <tbody>
                    {scalarFields.map(([label, field]) => (
                        <FieldRow key={field} label={label} from={before?.[field]} to={src?.[field]} op={op} />
                    ))}
                </tbody>
            </table>

            {/* Categories */}
            <div className="mb-2">
                <span className="text-muted small me-2">Categories</span>
                {displayCats !== null ? (
                    displayCats.length > 0
                        ? displayCats.map(c => <Badge bg="secondary" className="me-1" key={c}>{c}</Badge>)
                        : <span className="text-muted small">none</span>
                ) : (
                    <>
                        {sameCats.map(c => <Badge bg="secondary" className="me-1" key={c}>{c}</Badge>)}
                        {addedCats.map(c => <Badge bg="success" className="me-1" key={c}>+ {c}</Badge>)}
                        {removedCats.map(c => <Badge bg="danger" className="me-1" key={c}>− {c}</Badge>)}
                    </>
                )}
            </div>

            {/* Matches */}
            <MatchesSection before={beforeMatches} after={afterMatches} op={op} />

            {/* res_tags */}
            {hasResTags && (
                <div className="mb-2">
                    <div className="text-muted small mb-1">Res Tags</div>
                    {op === 'change'
                        ? <JsonDiffTree before={beforeResTags || {}} after={afterResTags || {}} emptyLabel="(empty)" />
                        : <pre style={{ fontSize: '0.75rem', maxHeight: 120, overflow: 'auto', background: '#111', color: op === 'add' ? '#86efac' : '#f87171', borderRadius: 4, padding: 6, margin: 0 }}>
                            {JSON.stringify(op === 'add' ? afterResTags : beforeResTags, null, 2)}
                          </pre>
                    }
                </div>
            )}

            {/* Presentation */}
            {hasPresentation && (
                <div className="mb-2">
                    <div className="text-muted small mb-1">Presentation</div>
                    {op === 'change'
                        ? <JsonDiffTree before={beforePresentation || {}} after={afterPresentation || {}} emptyLabel="(empty)" />
                        : <pre style={{ fontSize: '0.75rem', maxHeight: 120, overflow: 'auto', background: '#111', color: op === 'add' ? '#86efac' : '#f87171', borderRadius: 4, padding: 6, margin: 0 }}>
                            {JSON.stringify(op === 'add' ? afterPresentation : beforePresentation, null, 2)}
                          </pre>
                    }
                </div>
            )}
        </div>
    );
}

// ── Category diff ──────────────────────────────────────────────────────────

function CategoryDiff({ before, after, op }: { before: any; after: any; op: string }) {
    const src = op === 'delete' ? before : after;
    const scalarFields: [string, string][] = [
        ['Key', 'key'], ['Title', 'title'], ['Description', 'description'],
    ];
    const beforePresentation = before?.presentation ?? null;
    const afterPresentation = after?.presentation ?? null;
    const hasPresentation = (beforePresentation && Object.keys(beforePresentation).length > 0)
        || (afterPresentation && Object.keys(afterPresentation).length > 0);

    return (
        <div style={{ fontSize: '0.85rem' }}>
            <OpBanner op={op} />
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                <tbody>
                    {scalarFields.map(([label, field]) => (
                        <FieldRow key={field} label={label} from={before?.[field]} to={src?.[field]} op={op} />
                    ))}
                </tbody>
            </table>
            {hasPresentation && (
                <div className="mb-2">
                    <div className="text-muted small mb-1">Presentation</div>
                    {op === 'change'
                        ? <JsonDiffTree before={beforePresentation || {}} after={afterPresentation || {}} />
                        : <pre style={{ fontSize: '0.75rem', maxHeight: 120, overflow: 'auto', background: '#111', color: op === 'add' ? '#86efac' : '#f87171', borderRadius: 4, padding: 6, margin: 0 }}>
                            {JSON.stringify(op === 'add' ? afterPresentation : beforePresentation, null, 2)}
                          </pre>
                    }
                </div>
            )}
        </div>
    );
}

// ── Shared diff content (exported for hover panel) ─────────────────────────

type DiffMode = 'submitted' | 'current';

interface DiffRenderInput {
    before: any;
    after: any;
    op: 'add' | 'change' | 'delete';
    isCat: boolean;
    title: string;
}

function normalizeProposalOp(type: string): 'add' | 'change' | 'delete' {
    if (type === 'add') return 'add';
    if (type === 'delete') return 'delete';
    return 'change';
}

function computeOpFromBeforeAfter(before: any, after: any): 'add' | 'change' | 'delete' {
    const hasBefore = before !== null && before !== undefined;
    const hasAfter = after !== null && after !== undefined;
    if (!hasBefore && hasAfter) return 'add';
    if (hasBefore && !hasAfter) return 'delete';
    return 'change';
}

function getSubmittedDiffInput(change: PendingChange): DiffRenderInput {
    const detail = change.detail || {};
    const isCat = Boolean(detail.categoryKey || (detail.category && !detail.pvkey));
    const title = isCat
        ? `Category: ${detail.categoryKey || detail.after?.key || detail.before?.key || ''}`
        : `Fingerprint: ${detail.pvkey || detail.after?.pvkey || detail.before?.pvkey || ''}`;

    if (isCat) {
        return {
            before: detail.before,
            after: detail.after,
            op: normalizeProposalOp(change.proposal_type),
            isCat,
            title,
        };
    }

    // For merge proposals, `detail.fingerprint` has the full fp data; `detail.after` may be a summary.
    // Matches are stored separately at `detail.matches` (not inside before/after summaries), so we
    // attach them to the after object so MatchesSection can render them.
    const baseAfter = detail.fingerprint || detail.after;
    const afterWithMatches = baseAfter
        ? { ...baseAfter, matches: detail.matches || baseAfter.matches || [] }
        : baseAfter;
    return {
        before: detail.before,
        after: afterWithMatches,
        op: normalizeProposalOp(change.proposal_type),
        isCat,
        title,
    };
}

export function DiffContent({ change, override }: { change: PendingChange; override?: Partial<DiffRenderInput> }) {
    const submitted = getSubmittedDiffInput(change);
    const isCat = override?.isCat ?? submitted.isCat;
    const op = override?.op ?? submitted.op;
    const before = Object.prototype.hasOwnProperty.call(override || {}, 'before') ? override?.before : submitted.before;
    const after = Object.prototype.hasOwnProperty.call(override || {}, 'after') ? override?.after : submitted.after;

    if (isCat) {
        return <CategoryDiff before={before} after={after} op={op} />;
    }
    return <FingerprintDiff before={before} after={after} op={op} />;
}

// ── Main modal dialog ──────────────────────────────────────────────────────

interface MergeDiffDialogProps {
    change: PendingChange | null;
    onClose: () => void;
    defaultDiffMode?: DiffMode;
}

function MergeDiffDialog({ change, onClose, defaultDiffMode = 'submitted' }: MergeDiffDialogProps) {
    const [diffMode, setDiffMode] = useState<DiffMode>(defaultDiffMode);
    const [currentBefore, setCurrentBefore] = useState<any>(null);
    const [loadingCurrent, setLoadingCurrent] = useState(false);

    const submitted = useMemo(() => change ? getSubmittedDiffInput(change) : null, [change]);

    useEffect(() => {
        setDiffMode(defaultDiffMode);
        setCurrentBefore(null);
    }, [change?.id, defaultDiffMode]);

    useEffect(() => {
        const activeChange = change;
        if (!activeChange) return;
        if (diffMode !== 'current') return;

        let cancelled = false;

        async function loadCurrentDbVersion() {
            const c = activeChange as PendingChange;
            const detail = c.detail || {};
            const isCat = Boolean(detail.categoryKey || (detail.category && !detail.pvkey));
            setLoadingCurrent(true);
            try {
                if (isCat) {
                    const key = detail.categoryKey || detail.after?.key || detail.before?.key || '';
                    if (!key) {
                        if (!cancelled) setCurrentBefore(null);
                        return;
                    }
                    try {
                        const cat = await getCategory(key);
                        if (!cancelled) setCurrentBefore(cat || null);
                    } catch {
                        if (!cancelled) setCurrentBefore(null);
                    }
                    return;
                }

                let targetId = Number(c.fingerprint_id || 0);
                if (!(targetId > 0)) targetId = Number(c.target_fingerprint_id || 0);
                if (!(targetId > 0)) targetId = Number(detail.fingerprint_id || 0);
                if (!(targetId > 0)) targetId = Number(detail.target_fingerprint_id || 0);
                if (!(targetId > 0)) targetId = Number(detail.before?.fid || detail.after?.fid || 0);

                // Fallback for proposals without an id link: scan by pvkey to find current target.
                if (!(targetId > 0)) {
                    const pvkey = detail.pvkey || detail.after?.pvkey || detail.before?.pvkey || '';
                    if (pvkey) {
                        const all = await getFingerprintsThin();
                        const found = (all || []).find((fp: any) => fp?.pvkey === pvkey);
                        if (found?.fid) targetId = Number(found.fid);
                    }
                }

                if (!(targetId > 0)) {
                    if (!cancelled) setCurrentBefore(null);
                    return;
                }

                const fp = await getFingerprint(targetId);
                const matches = await getMatchesForFinger(targetId);
                const fullFp = {
                    ...fp,
                    matches: Array.isArray(matches) ? matches : [],
                };
                if (!cancelled) setCurrentBefore(fullFp);
            } catch {
                if (!cancelled) setCurrentBefore(null);
            } finally {
                if (!cancelled) setLoadingCurrent(false);
            }
        }

        loadCurrentDbVersion();
        return () => {
            cancelled = true;
        };
    }, [change, diffMode]);

    if (!change || !submitted) return null;

    const currentOverride: Partial<DiffRenderInput> = {
        before: currentBefore,
        after: submitted.op === 'delete' ? null : submitted.after,
        op: computeOpFromBeforeAfter(currentBefore, submitted.op === 'delete' ? null : submitted.after),
        isCat: submitted.isCat,
    };

    return (
        <Modal show={Boolean(change)} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton style={{ background: '#161b22', borderBottom: '1px solid #30363d' }}>
                <Modal.Title style={{ fontSize: '0.9rem', fontFamily: 'monospace', color: '#e6edf3' }}>
                    {submitted.title}
                    <span className="ms-2 text-muted" style={{ fontSize: '0.75rem', fontFamily: 'sans-serif' }}>
                        {diffMode === 'submitted' ? submitted.op : currentOverride.op}
                    </span>
                </Modal.Title>
                <ButtonGroup size="sm" className="ms-auto me-3">
                    <Button
                        variant={diffMode === 'submitted' ? 'primary' : 'outline-secondary'}
                        onClick={() => setDiffMode('submitted')}
                    >
                        Submitted Diff
                    </Button>
                    <Button
                        variant={diffMode === 'current' ? 'primary' : 'outline-secondary'}
                        onClick={() => setDiffMode('current')}
                    >
                        Current DB Diff
                    </Button>
                </ButtonGroup>
            </Modal.Header>
            <Modal.Body style={{ background: '#0d1117', color: '#e6edf3' }}>
                {diffMode === 'current' && loadingCurrent && (
                    <div className="text-muted small mb-2">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading current DB state...
                    </div>
                )}
                <DiffContent change={change} override={diffMode === 'submitted' ? undefined : currentOverride} />
            </Modal.Body>
            <Modal.Footer style={{ background: '#161b22', borderTop: '1px solid #30363d' }}>
                <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default MergeDiffDialog;
